import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import PortalLocationSelects from "./PortalLocationSelects";
import PortalSourceSelect from "./PortalSourceSelect";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { canDeleteInPortal } from "./portalSession";
import PortalAlert from "./PortalAlert";
import PortalConfirmModal from "./PortalConfirmModal";
import PortalFormPage from "./PortalFormPage";
import PortalResultsHead from "./PortalResultsHead";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { formatPhone } from "../utils/formatters";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import "../assets/css/DigixLeads.css";
import PortalSelect from "./PortalSelect";

const PORTAL_CUSTOMERS_ROWS_PER_PAGE_KEY = "portalCustomersRowsPerPage";
const PORTAL_CUSTOMERS_CURRENT_PAGE_KEY = "portalCustomersCurrentPage";

const CUSTOMER_STATUSES = ["Active", "Inactive"];
const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "+91",
  phone: "",
  altPhone: "",
  addressLine1: "",
  addressLine2: "",
  countryId: "",
  stateId: "",
  cityId: "",
  zipCode: "",
  source: "",
  comLang: "",
  status: "Active",
  notes: "",
  projectName: "",
  roomNo: "",
  bed: "",
  sharingType: "",
  rent: "",
  moveInDate: "",
  emergencyContact: "",
};

function projectOptionLabel(project) {
  return [project.Short_Code, project.Project_Name].filter(Boolean).join(" — ");
}

function formatDateDisplay(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function fullName(row) {
  return `${(row.First_Name || "").trim()} ${(row.Last_Name || "").trim()}`.trim() || "—";
}

function phoneDisplay(row) {
  if (!row.Phone) return "—";
  const digits = `${row.Country_Code || ""}${row.Phone}`.replace(/\D/g, "");
  return formatPhone(digits) || "—";
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

const SORT_ACCESSORS = {
  name: (c) => fullName(c).toLowerCase(),
  project: (c) => (c.Project_Name || "").toLowerCase(),
  email: (c) => (c.Email || "").toLowerCase(),
  phone: (c) => phoneDisplay(c),
  status: (c) => (c.Status || "").toLowerCase(),
  source: (c) => (c.Source || "").toLowerCase(),
  moveInDate: (c) => (c.Move_In_Date ? new Date(c.Move_In_Date).getTime() || 0 : 0),
};

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "project", label: "Project" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "source", label: "Source" },
  { key: "moveInDate", label: "Move-in" },
  { key: "status", label: "Status" },
];

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(preset, fromDate, toDate) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "7": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return { from, to: endOfDay(now) };
    }
    case "30": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 29);
      return { from, to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: fromDate ? startOfDay(new Date(fromDate)) : null,
        to: toDate ? endOfDay(new Date(toDate)) : null,
      };
    default:
      return { from: null, to: null };
  }
}

function TenantCustomers({ createMode = false }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_CUSTOMERS_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_CUSTOMERS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [sourceOptions, setSourceOptions] = useState([]);
  const [tenantProjects, setTenantProjects] = useState([]);
  const [idProofFile, setIdProofFile] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [deactivateCustomer, setDeactivateCustomer] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_CUSTOMERS_ROWS_PER_PAGE_KEY, String(next));
  };

  const loadCustomers = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-customers");
    setCustomers(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  useEffect(() => {
    if (createMode) return undefined;
    let active = true;
    (async () => {
      try {
        await loadCustomers();
      } catch (_) {
        if (active) setError("Could not load customers.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadCustomers, createMode]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/lead-meta");
        if (!active) return;
        const data = res.data?.data || {};
        const sources = Array.isArray(data.sources) ? data.sources : [];
        setSourceOptions(
          sources
            .map((s) => s.Source_Name ?? s.source_name ?? "")
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
        );
        setTenantProjects(Array.isArray(data.tenantProjects) ? data.tenantProjects : []);
      } catch (_) {
        if (active) {
          setSourceOptions([]);
          setTenantProjects([]);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const cityOptions = useMemo(
    () =>
      Array.from(new Set(customers.map((c) => c.City_Name).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [customers]
  );

  const filtered = useMemo(() => {
    let list = customers;

    const { from, to } = presetRange(datePreset, fromDate, toDate);
    if (from || to) {
      list = list.filter((c) => {
        if (!c.Created_On) return false;
        const created = new Date(c.Created_On);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
    }

    if (statusFilter) list = list.filter((c) => c.Status === statusFilter);
    if (sourceFilter) list = list.filter((c) => c.Source === sourceFilter);
    if (cityFilter) list = list.filter((c) => c.City_Name === cityFilter);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [
          c.First_Name,
          c.Last_Name,
          c.Email,
          c.Phone,
          c.Alt_Phone,
          c.Country_Code,
          c.Source,
          c.Project_Name,
          c.Room_No,
          c.City_Name,
          c.State_Name,
          c.Country_Name,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [customers, search, datePreset, fromDate, toDate, statusFilter, sourceFilter, cityFilter]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((c) =>
      [
        c.First_Name,
        c.Last_Name,
        c.Email,
        c.Phone,
        c.Alt_Phone,
        c.Country_Code,
        c.Source,
        c.Project_Name,
        c.Room_No,
        c.City_Name,
        c.State_Name,
        c.Country_Name,
        c.Status,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const hasActiveFilters =
    datePreset !== "all" ||
    !!statusFilter ||
    !!sourceFilter ||
    !!cityFilter ||
    !!search;

  const clearFilters = () => {
    setDatePreset("all");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSourceFilter("");
    setCityFilter("");
    setSearch("");
    setCurrentPage(1);
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return resultFiltered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...resultFiltered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = sorted.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (currentPage > 0) {
      localStorage.setItem(PORTAL_CUSTOMERS_CURRENT_PAGE_KEY, String(currentPage));
    }
  }, [currentPage]);

  const skipFilterPageResetRef = React.useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, datePreset, fromDate, toDate, statusFilter, sourceFilter, cityFilter]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  useEffect(() => {
    if (!createMode) return;
    setForm(EMPTY_FORM);
    setIdProofFile(null);
    setFormError("");
  }, [createMode]);

  const closeAddForm = () => navigate("/portal/customers");

  const buildPayload = () => ({
    First_Name: form.firstName.trim(),
    Last_Name: form.lastName.trim() || null,
    Email: form.email.trim() || null,
    Country_Code: form.countryCode.trim() || null,
    Phone: form.phone.trim() || null,
    Alt_Phone: form.altPhone.trim() || null,
    Address_Line_1: form.addressLine1.trim() || null,
    Address_Line_2: form.addressLine2.trim() || null,
    Country_Id: form.countryId ? Number(form.countryId) : null,
    State_Id: form.stateId ? Number(form.stateId) : null,
    City_Id: form.cityId ? Number(form.cityId) : null,
    Zip_Code: form.zipCode.trim() || null,
    Source: form.source.trim() || null,
    Com_Lang: form.comLang.trim() || null,
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
    Project_Name: form.projectName.trim() || null,
    Room_No: form.roomNo.trim() || null,
    Bed: form.bed.trim() || null,
    Sharing_Type: form.sharingType.trim() || null,
    Rent: form.rent.trim() || null,
    Move_In_Date: form.moveInDate || null,
    Emergency_Contact: form.emergencyContact.trim() || null,
  });

  const buildRequestBody = () => {
    const payload = buildPayload();
    if (!idProofFile) return payload;
    const fd = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      fd.append(key, value == null ? "" : String(value));
    });
    fd.append("Id_Proof", idProofFile);
    return fd;
  };

  const requestDeactivate = (customer) => {
    if (!canDelete || !customer?.Tenant_Customer_Id || customer.Status === "Inactive") return;
    setDeactivateCustomer(customer);
  };

  const closeDeactivateModal = () => {
    if (deactivatingId) return;
    setDeactivateCustomer(null);
  };

  const confirmDeactivate = async () => {
    const customer = deactivateCustomer;
    if (!canDelete || !customer?.Tenant_Customer_Id) return;

    setDeactivatingId(customer.Tenant_Customer_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/tenant-customers/${customer.Tenant_Customer_Id}`);
      await loadCustomers();
      setDeactivateCustomer(null);
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate customer.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) {
      setFormError("First name is required.");
      return;
    }
    const phoneError = mobileValidationMessage(form.phone, {
      required: true,
      label: "Mobile number",
    });
    if (phoneError) {
      setFormError(phoneError);
      return;
    }
    const altPhoneError = mobileValidationMessage(form.altPhone, { label: "Alternate phone" });
    if (altPhoneError) {
      setFormError(altPhoneError);
      return;
    }
    if (!form.source.trim()) {
      setFormError("Source is required.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/tenant-customers", buildRequestBody());
      closeAddForm();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create customer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      {!createMode && (
        <>
      <div className="cportal-page-head">
        <h1>Customers</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, phone, email"
        searchAriaLabel="Search customers"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        actions={
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/customers/new")}
          >
            Add Customer
          </button>
        }
      >
        <div className="cportal-filter-presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cportal-chip ${datePreset === p.id ? "active" : ""}`}
              onClick={() => setDatePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="cportal-filter-dates">
            <label className="cportal-field">
              <span>From date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="cportal-field">
              <span>To date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
        )}

        <div className="cportal-filter-row">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All status</option>
            {CUSTOMER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <option value="">All cities</option>
            {cityOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading="Customers"
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {customers.length === 0 ? "No customers added yet." : "No customers match your filters."}
          </p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((customer) => (
                  <tr key={customer.Tenant_Customer_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() =>
                          navigate(`/portal/customers/${customer.Tenant_Customer_Id}`)
                        }
                      >
                        {fullName(customer)}
                      </button>
                    </td>
                    <td>{display(customer.Project_Name)}</td>
                    <td>{display(customer.Email)}</td>
                    <td>{phoneDisplay(customer)}</td>
                    <td>{display(customer.Source)}</td>
                    <td>{formatDateDisplay(customer.Move_In_Date)}</td>
                    <td>
                      <span className={statusBadgeClass(customer.Status)}>
                        {display(customer.Status)}
                      </span>
                    </td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() =>
                            navigate(`/portal/customers/${customer.Tenant_Customer_Id}`)
                          }
                          title="View customer"
                          aria-label="View customer"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/customers/${customer.Tenant_Customer_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit customer"
                          aria-label="Edit customer"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => requestDeactivate(customer)}
                            disabled={
                              customer.Status === "Inactive" ||
                              deactivatingId === customer.Tenant_Customer_Id
                            }
                            title={
                              customer.Status === "Inactive"
                                ? "Already inactive"
                                : "Deactivate customer"
                            }
                            aria-label="Deactivate customer"
                          >
                            {deactivatingId === customer.Tenant_Customer_Id ? "…" : <FaTrash size={16} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList
            emptyLabel={
              customers.length === 0
                ? "No customers added yet."
                : "No customers match your filters."
            }
            className="cportal-customers-mobile"
          >
            {pageRows.map((customer) => {
              const id = customer.Tenant_Customer_Id;
              const status = customer.Status || "—";
              const statusTone =
                String(status).toLowerCase() === "active"
                  ? "success"
                  : String(status).toLowerCase() === "inactive"
                    ? "danger"
                    : "neutral";
              const moveIn = formatDateDisplay(customer.Move_In_Date);
              return (
                <ResultsMobileCard
                  key={id}
                  title={fullName(customer)}
                  subtitle={
                    customer.Project_Name ||
                    (phoneDisplay(customer) !== "—" ? phoneDisplay(customer) : null)
                  }
                  meta={[
                    { label: status, tone: statusTone, status: true },
                    customer.Source ? { label: customer.Source, tone: "info" } : null,
                  ].filter(Boolean)}
                  fields={[
                    { label: "Project", value: customer.Project_Name },
                    { label: "Email", value: customer.Email },
                    {
                      label: "Phone",
                      value: phoneDisplay(customer) !== "—" ? phoneDisplay(customer) : null,
                    },
                    { label: "Move-in", value: moveIn !== "—" ? moveIn : null },
                  ]}
                  onOpen={() => navigate(`/portal/customers/${id}`)}
                  actions={
                    <div className="co-mcard__action-grid">
                      <button
                        type="button"
                        className="co-maction"
                        title="View"
                        aria-label="View"
                        onClick={() => navigate(`/portal/customers/${id}`)}
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() =>
                          navigate(`/portal/customers/${id}`, { state: { edit: true } })
                        }
                      >
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Deactivate"
                          aria-label="Deactivate"
                          disabled={
                            customer.Status === "Inactive" || deactivatingId === id
                          }
                          onClick={() => requestDeactivate(customer)}
                        >
                          <FaTrash size={14} />
                          <span>Remove</span>
                        </button>
                      ) : null}
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {sorted.length > 0 && !loading && (
          <TablePagination
            currentPage={currentPage}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="customers"
            rowsPerPageSelectId="portal-customers-rows-per-page"
          />
        )}
      </div>

      <PortalConfirmModal
        open={!!deactivateCustomer}
        title="Deactivate Customer"
        message={
          deactivateCustomer ? (
            <p>
              Deactivate <strong>{fullName(deactivateCustomer)}</strong>? Their status will be set to
              Inactive.
            </p>
          ) : null
        }
        confirming={!!deactivatingId}
        confirmingLabel="Deactivating…"
        onConfirm={confirmDeactivate}
        onClose={closeDeactivateModal}
      />

        </>
      )}

      {createMode && (
        <PortalFormPage
          title="Add Customer"
          backLabel="Back to customers"
          onBack={closeAddForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Create customer"
          submittingLabel="Creating…"
        >
              <PortalAlert type="error" message={formError} onClear={() => setFormError("")} />
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>
                    First name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setField("firstName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Last name</label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setField("lastName", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                </div>
                <div className="cportal-field cportal-field-phone">
                  <label>
                    Mobile number <span className="cportal-required">*</span>
                  </label>
                  <div className="cportal-phone-row">
                    <input
                      className="cportal-phone-code"
                      value={form.countryCode}
                      onChange={(e) => setField("countryCode", e.target.value)}
                      placeholder="+91"
                    />
                    <input
                      value={form.phone}
                      onChange={(e) => setField("phone", sanitizeMobileInput(e.target.value))}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit number"
                      required
                    />
                  </div>
                </div>
                <div className="cportal-field">
                  <label>Emergency contact</label>
                  <input
                    value={form.emergencyContact}
                    onChange={(e) => setField("emergencyContact", e.target.value)}
                    placeholder="Name"
                  />
                </div>
                <div className="cportal-field">
                  <label>Alt phone</label>
                  <input
                    value={form.altPhone}
                    onChange={(e) => setField("altPhone", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                  />
                </div>
                <PortalSourceSelect
                  value={form.source}
                  onChange={(value) => setField("source", value)}
                  required
                />
                <div className="cportal-field">
                  <label>Communication language</label>
                  <input
                    value={form.comLang}
                    onChange={(e) => setField("comLang", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect value={form.status} onChange={(e) => setField("status", e.target.value)}>
                    {CUSTOMER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
              </div>

              <h3 className="cportal-form-section">Address</h3>
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>Address line 1</label>
                  <input
                    value={form.addressLine1}
                    onChange={(e) => setField("addressLine1", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Address line 2</label>
                  <input
                    value={form.addressLine2}
                    onChange={(e) => setField("addressLine2", e.target.value)}
                  />
                </div>
                <PortalLocationSelects
                  countryId={form.countryId}
                  stateId={form.stateId}
                  cityId={form.cityId}
                  onChange={({ countryId, stateId, cityId }) =>
                    setForm((f) => ({ ...f, countryId, stateId, cityId }))
                  }
                />
                <div className="cportal-field">
                  <label>Zip code</label>
                  <input
                    value={form.zipCode}
                    onChange={(e) => setField("zipCode", e.target.value)}
                  />
                </div>
              </div>

              <h3 className="cportal-form-section">Stay Details</h3>
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>Project name</label>
                  <PortalSelect
                    value={form.projectName}
                    onChange={(e) => setField("projectName", e.target.value)}
                  >
                    <option value="">Select project</option>
                    {tenantProjects.map((project) => (
                      <option key={project.Tenant_Project_Id} value={project.Project_Name}>
                        {projectOptionLabel(project)}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field">
                  <label>Room no</label>
                  <input
                    value={form.roomNo}
                    onChange={(e) => setField("roomNo", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Bed</label>
                  <input
                    value={form.bed}
                    onChange={(e) => setField("bed", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Sharing type</label>
                  <input
                    value={form.sharingType}
                    onChange={(e) => setField("sharingType", e.target.value)}
                    placeholder="e.g. 2 sharing"
                  />
                </div>
                <div className="cportal-field">
                  <label>Rent</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.rent}
                    onChange={(e) => setField("rent", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Move-in date</label>
                  <input
                    type="date"
                    value={form.moveInDate}
                    onChange={(e) => setField("moveInDate", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>ID proof</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={(e) => setIdProofFile(e.target.files?.[0] || null)}
                  />
                </div>
              </div>

              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field cportal-field--full">
                  <label>Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
              </div>
        </PortalFormPage>
      )}
    </div>
  );
}

export default TenantCustomers;
